import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Button, type ButtonSize } from '../primitives/button.js';
import { Tooltip } from '../primitives/tooltip.js';

export interface DisabledActionProps {
  label: string;
  /** Why the action is unavailable. Always exposed to assistive tech via aria-describedby. */
  reason: string;
  icon?: React.ReactNode;
  size?: Extract<ButtonSize, 'sm' | 'md'>;
  /**
   * 'inline' (default) prints the reason under the button. 'tooltip' keeps it compact: the reason shows on
   * hover/focus of a focusable wrapper (disabled buttons cannot receive focus) and stays in the accessible description.
   */
  display?: 'inline' | 'tooltip';
  className?: string;
}

/**
 * A disabled action with its reason available, for capabilities that are not available yet or not verified.
 * Honest by design: no fake buttons.
 */
export function DisabledAction({
  label,
  reason,
  icon,
  size = 'md',
  display = 'inline',
  className,
}: DisabledActionProps) {
  const id = React.useId();
  const button = (
    <Button
      variant="secondary"
      size={size}
      disabled
      aria-describedby={id}
      leadingIcon={icon}
    >
      {label}
    </Button>
  );
  if (display === 'tooltip') {
    return (
      <span className={className}>
        <Tooltip content={reason}>
          <span
            tabIndex={0}
            aria-label={`${label} (no disponible)`}
            aria-describedby={id}
            className="inline-flex rounded-control focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
          >
            {button}
          </span>
        </Tooltip>
        <span id={id} className="sr-only">
          {reason}
        </span>
      </span>
    );
  }
  return (
    <div className={cn('grid justify-items-start gap-1', className)}>
      {button}
      <p id={id} className="max-w-sm text-meta text-text-secondary">
        {reason}
      </p>
    </div>
  );
}
